import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ImpersonationBroker, type IPagedCollection, asApiError } from '../../../extViewmodels';
import type { ITextMessage } from '../../../extViewmodels/TextMessagingViewModel';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

const PAGE_SIZE = 50;
const REFETCH_INTERVAL_MS = 10_000;

const sentAt = (message: ITextMessage) => message.sentDate ?? message.creationDate ?? '';

/**
 * Most recent page of messages in a conversation, oldest first. Polls while
 * enabled so a reply shows up in the dialog without the main app's STOMP feed,
 * which the Coffee bundle does not run.
 */
export function useTextMessages(
	conversationId: string | undefined,
	{ enabled = true }: { enabled?: boolean } = {}
): UseQueryResult<ITextMessage[], Error> {
	const userSession = useUserSession();

	return useQuery<ITextMessage[], Error>({
		queryKey: queryKeys.focusedView.textMessages(conversationId),
		enabled: enabled && !!conversationId,
		refetchInterval: enabled ? REFETCH_INTERVAL_MS : false,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IPagedCollection<ITextMessage>>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `conversation/${conversationId}/messages`,
					queryParams: { pageSize: PAGE_SIZE },
				}),
				'GET'
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return [...(result.value?.values ?? [])].sort((a, b) => sentAt(a).localeCompare(sentAt(b)));
		},
		meta: { errorLogging: { category: 'FocusedViewTexting', action: 'TextMessagesQuery' } },
	});
}
